<template>
  <div class="floating-container">
    <div class="flex justify-between">
      <div class="breadcrumb">
        <div class="inline-block h-full mt-0.5">
          <ol
            v-if="responsiveBreakpointSm"
            class="list-none p-0 inline-grid grid-flow-col app-navigation-breadcrumb h-full items-center"
          >
            <li
              v-if="displayHome"
              class="inline-grid grid-flow-col app-navigation-breadcrumb-item items-center"
            >
              <router-link :to="homeMenu.getRouterTo()" class="text-clickable">
                {{ t('hit-app.common.home') }}
              </router-link>
            </li>
            <hit-icon icon="expand-right" color="text-background" size="sm" />

            <li
              v-for="level in parentLevels"
              :key="level.label"
              class="inline-grid grid-flow-col app-navigation-breadcrumb-item items-center"
            >
              <router-link
                :to="{name: level.route, params: level.routeParams}"
                class="text-clickable"
              >
                {{ level.label }}
              </router-link>
              <hit-icon icon="expand-right" size="sm" color="text-background" />
            </li>
            <span v-if="currentLevel" class="text-background truncate">{{
              currentLevel.label
            }}</span>
          </ol>
          <div v-if="!responsiveBreakpointSm">
            <div class="flex flex-row gap-1 mt-0">
              <hit-button
                prefix-icon="back"
                :remove-margin="true"
                color="button-floating"
                @click="$router.go(-1)"
              />
              <hit-dropdown
                icon="arrow_down"
                icon-size="xs"
                color="button-floating"
                :items="navigationDropdownItems"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!this.responsiveBreakpointSm"
        id="action-buttons"
        class="action-buttons ml-auto"
      />
    </div>
    <Teleport v-if="teleportReady" to="#action-buttons">
      <div class="actions inline-grid gap-1 items-center ml-2 grid-flow-col">
        <hit-dropdown
          v-if="canvas && canvas.showReorderButtons"
          class="inline-block text-light-2"
          :label="t('hit-app.common.column', 2)"
          icon="two_columns"
          icon-size="xs"
          button-color="button-floating"
          :remove-padding="false"
          :hide-border="false"
          :white-background="true"
          :items="[
            new DropdownItem({
              label: t('hit-app.common.dynamic'),
              color: !canvas.forcedNumberColumns ? 'accent' : undefined,
              clickEventHandler: () => canvas.setNumberColumns(0),
            }),
            new DropdownItem({
              label: '1 ' + t('hit-app.common.column', 1),
              color: canvas.forcedNumberColumns === 1 ? 'accent' : undefined,
              clickEventHandler: () => canvas.setNumberColumns(1),
            }),
            new DropdownItem({
              label: '2 ' + t('hit-app.common.column', 2),
              color: canvas.forcedNumberColumns === 2 ? 'accent' : undefined,
              clickEventHandler: () => canvas.setNumberColumns(2),
            }),
            new DropdownItem({
              label: '3 ' + t('hit-app.common.column', 2),
              color: canvas.forcedNumberColumns === 3 ? 'accent' : undefined,
              clickEventHandler: () => canvas.setNumberColumns(3),
            }),
            new DropdownItem({
              label: '4 ' + t('hit-app.common.column', 2),
              color: canvas.forcedNumberColumns === 4 ? 'accent' : undefined,
              clickEventHandler: () => canvas.setNumberColumns(4),
            }),
          ]"
        />

        <hit-button
          v-if="customActions.length === 1"
          :key="customActions[0].id"
          :prefix-icon="customActions[0].icon"
          :color="customActions[0].buttonColor"
          :label="customActions[0].label"
          :handle-middle-click-separately="true"
          :custom-bg-color="
            customActions[0].buttonColor.includes('#')
              ? customActions[0].buttonColor
              : null
          "
          @click="onActionClick($event, customActions[0])"
          @middleClick="onActionClick($event, customActions[0], true)"
        />
        <hit-dropdown
          v-else-if="customActions.length > 0"
          :key="customActions[0]"
          :icon="customActions[0].icon"
          :label="t('hit-app.common.custom-actions')"
          :custom-bg-color="
            customActions[0].buttonColor.includes('#')
              ? customActions[0].buttonColor
              : null
          "
          :hide-border="false"
          icon-size="xs"
          :items="customActionsDropdownItems"
        />

        <template v-for="action of actionButtons">
          <template v-if="action.splitButton && action.subActions.length > 0">
            <div
              :key="action.id"
              class="inline-flex items-stretch"
              :class="{hidden: action.disabled}"
            >
              <hit-button
                :prefix-icon="action.icon"
                :label="getActionLabel(action)"
                :color="action.buttonColor"
                :handle-middle-click-separately="true"
                :custom-bg-color="
                  action.buttonColor.includes('#') ? action.buttonColor : null
                "
                :disabled="action.disabled"
                :hide-border="false"
                :white-background="true"
                specific-icon-color="#374151"
                icon-size="xs"
                :show-spinner="action.spinner"
                :tooltip="action.tooltip"
                class="!rounded-r-none"
                @click="onActionClick($event, action)"
                @middleClick="onActionClick($event, action, true)"
              />
              <hit-dropdown
                icon="arrow_down"
                icon-size="xs"
                :button-color="action.buttonColor"
                :disabled="action.disabled"
                :hide-border="false"
                :white-background="true"
                specific-icon-color="#374151"
                position-anchor="parent"
                horizontal-align="end"
                class="split-button-chevron"
                :items="
                  action.subActions.map(
                    (sa) =>
                      new DropdownItem({
                        label: sa.label,
                        icon: sa.icon,
                        disabled: sa.disabled,
                        separatorAfter: !isLastSubAction(action, sa),
                        clickEventHandler: ($event) =>
                          onActionClick($event, sa),
                      }),
                  )
                "
              />
            </div>
          </template>
          <template v-else-if="action.subActions.length > 0">
            <hit-dropdown
              :key="action.id"
              :prefix-icon="action.icon"
              :label="getActionLabel(action)"
              :button-color="action.buttonColor"
              :icon="action?.icon"
              :disabled="action.disabled"
              :hidden="action.disabled"
              :hide-border="false"
              specific-icon-color="#374151"
              icon-size="xs"
              :items="
                action.subActions.map(
                  (sa) =>
                    new DropdownItem({
                      label: sa.label,
                      icon: sa.icon,
                      disabled: sa.disabled,
                      separatorAfter: !isLastSubAction(action, sa),
                      clickEventHandler: ($event) => onActionClick($event, sa),
                    }),
                )
              "
            />
          </template>
          <template v-else>
            <hit-button
              :key="action.id"
              :prefix-icon="action.icon"
              :label="getActionLabel(action)"
              :color="action.buttonColor"
              :handle-middle-click-separately="true"
              :custom-bg-color="
                action.buttonColor.includes('#') ? action.buttonColor : null
              "
              :disabled="action.disabled"
              :hide-border="false"
              :white-background="true"
              specific-icon-color="#374151"
              icon-size="xs"
              :show-spinner="action.spinner"
              :tooltip="action.tooltip"
              @click="onActionClick($event, action)"
              @middleClick="onActionClick($event, action, true)"
            />
          </template>
        </template>

        <hit-dropdown
          v-if="otherMenuActions.length > 0"
          class="inline-block text-light-2"
          icon="option-menu"
          icon-size="xs"
          button-color="button-floating"
          :remove-padding="false"
          :hide-border="false"
          :white-background="true"
          icon-color="#374151"
          :items="
            otherMenuActions.map(
              (oa) =>
                new DropdownItem({
                  label: oa.label,
                  icon: oa.icon,
                  separatorAfter: !isLastAction(oa),
                  clickEventHandler: ($event) => onActionClick($event, oa),
                }),
            )
          "
        />
      </div>
    </Teleport>
    <div class="floating-bottom">
      <div
        class="text-background text-2xl font-bold truncate flex items-center gap-2"
        :class="{'mt-1.5': !responsiveBreakpointSm}"
      >
        <span>{{ selectedInstanceDesignationToDisplay }}</span>
        <hit-icon
          v-if="hasPassedModelInstance && isReadOnlyImportedInstance"
          icon="lock"
          :tooltip="readOnlyImportedTooltip"
        />
        <hit-icon
          v-if="locked"
          icon="lock"
          :tooltip="t('hit-app.project-part.locked-project-part')"
        />
      </div>
      <div v-if="responsiveBreakpointSm">
        <slot name="extra" />
      </div>
      <div
        v-if="this.responsiveBreakpointSm"
        id="action-buttons"
        class="action-buttons"
      />
    </div>
    <div v-if="!responsiveBreakpointSm">
      <slot name="extra" />
      <div
        v-if="visibleCallToActionButtons.length > 0"
        class="grid gap-2 grid-flow-row app-cards mt-2"
      >
        <div
          v-for="action in visibleCallToActionButtons"
          :key="action.id"
          class="floating-call-to-action-card grid min-h-16 w-full border border-table rounded-md cursor-pointer"
          :class="action.hoverBorderColorClass"
          @click="onCallToActionClick(action)"
        >
          <div
            class="grid items-center justify-center w-16 text-white text-sm leading-5 font-medium rounded-l-md"
            :class="action.backgroundColorClass"
          >
            <hit-icon :icon="action.icon" :color="action.iconColor" size="md" />
          </div>
          <div class="bg-table border-l border-table rounded-r-md">
            <div class="pl-4 py-2 grid items-center h-full">
              <div class="text-lg leading-normal">{{ action.label }}</div>
              <div v-if="action.description" class="text-sm text-light">
                {{ action.description }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="iframeData && iframeData.src" :key="iframeData.src" class="mt-2">
      <hit-top-iframe
        v-if="iframeData.type === 'top'"
        :src="iframeData.src"
        :title="iframeData.title"
        @close="iframeData = null"
      />
      <hit-popup-iframe
        v-else-if="iframeData.type === 'popup'"
        :src="iframeData.src"
        :title="iframeData.title"
        @cancel="onPopupIframeCancel"
        @continue="onPopupIframeContinue"
        @navigate="onPopupIframeNavigate"
        @refresh-parent="onPopupIframeRefresh"
        @notify="onPopupIframeNotify"
        @set-title="onPopupIframeSetTitle"
        @open-url="onPopupIframeOpenUrl"
        @download-file="onPopupIframeDownloadFile"
      />
      <hit-tab-iframe v-else />
    </div>
    <create-item-dialog
      v-if="showCreateDialog || forceCreateItemDialogVisible"
      visible
      :router-route="entityClass.detailPageRoute"
      :api-route="HitUtils.camelToSnake(entityClass.apiRoute)"
      :init-cols="{
        ...entityClass.createInitColumns,
        ...customInitCols,
      }"
      :tab="tab"
      @hide="hideCreateItemDialog"
    />
    <hit-dialog
      v-model:visible="showDeleteDialog"
      size="small"
      :title="t('hit-base.common.delete-dialog-title')"
      color="danger"
      :message="t('hit-base.common.delete-dialog-message')"
      :ok-button-label="t('hit-components.common.delete')"
      ok-button-icon="trash"
      ok-button-color="danger"
      cancel-button-icon="clear"
      :cancel-button-label="t('hit-components.common.cancel')"
      :show-cancel-button="true"
      @ok="deleteEntity"
      @cancel="showDeleteDialog = false"
    />
    <hit-app-show-created-modified
      v-if="showCreationModificationPopup"
      :table="entityClass.apiRoute"
      :item-id="instanceId"
      :is-staff-table="entityClass.apiRoute === 'staff'"
      @close="showCreationModificationPopup = false"
    />
    <hit-app-print-config
      v-if="showPrintDialog"
      :ids-to-print="[detailPrintId]"
      :template-type="
        forcePrintTemplateType || selectedInstance?.printTemplateType
      "
      :custom-template-filter-fn="customTemplateFilterFn"
      :custom-configs-initial-values="printConfigsInitialValues"
      :print-config-options="printConfigOptions"
      @cancel="showPrintDialog = false"
      @printed="showPrintDialog = false"
    />
    <hit-app-import-excel
      v-if="showImportDialog"
      :table-name="entityClass.apiRoute"
      :linked-record-id="instanceId"
      @cancel="showImportDialog = false"
      @importSuccess="
        showImportDialog = false;
        $emit('imported');
      "
    />
    <hit-dynamic-browser
      v-if="browserModalVisible"
      visible
      :browser-type="HitUtils.snakeToCamel(entityClass.apiRoute)"
      :filter-id-column="browserFilterColumn"
      :filter-id="browserFilterValue"
      @cancel="browserModalVisible = false"
      @item-selected="
        (item) => {
          browserModalVisible = false;
          redirectToBrowsedItem(item);
        }
      "
    />
    <save-as-favorite-modal
      v-if="showFavoriteModal"
      @cancel="showFavoriteModal = null"
      @finished="showFavoriteModal = null"
    />

    <hit-modal
      v-if="showHidePanelsModal"
      prefix-icon="show-off"
      :title="t('hit-app.common.hide-panels')"
      :message="t('hit-app.common.hide-panels-message')"
      popup-size="small"
      @cancel="showHidePanelsModal = false"
    >
      <div class="mb-2">
        <span class="text-panel">{{
          t('hit-app.common.hide-panels-message')
        }}</span>
      </div>
      <div v-for="panel of allViewPanels" :key="panel.key">
        <div class="hit-form">
          <hit-input-checkbox
            :values="t(panel.key)"
            :value="!panel.hidden"
            :add-bottom-margin="false"
            @change="togglePanel(panel)"
          />
        </div>
      </div>
      <hit-button
        :label="t('hit-components.common.save')"
        full-width
        color="accent"
        @click="saveHidePanels"
      />
    </hit-modal>
    <hit-modal
      v-if="showDuplicateDialog"
      prefix-icon="duplicate"
      :title="t('hit-app.common.duplicate')"
      popup-size="small"
      @cancel="showDuplicateDialog = false"
    >
      <span class="text-panel mb-2">{{
        t('hit-app.common.duplicate-message')
      }}</span>
      <hit-input-radio-button
        :options="optionsDuplicate"
        :value="duplicateValue"
        :hide-clear-button="true"
        @change="(v) => (duplicateValue = v)"
      />
      <div class="text-right">
        <hit-button
          :label="t('hit-app.common.duplicate')"
          prefix-icon="duplicate"
          color="accent"
          @click="duplicateRecord"
        />
      </div>
    </hit-modal>
  </div>
</template>

<script>
import {
  HitBreakpointsMixin,
  HitButton,
  HitDialog,
  HitDropdown,
  DropdownItem,
  HitIcon,
  HitModal,
  HitInputCheckbox,
  HitInputRadioButton,
} from '@hit/components/src';
import {useI18n} from 'vue-i18n';
import HitActionItem from '../action/HitActionItem';
import HitAppShowCreatedModified from '../../app/common/HitAppShowCreatedModified.vue';
import CreateItemDialog from '../dialog/CreateItemDialog.vue';
import HitDynamicBrowser from '@hit/base/src/components/hit/browser/HitDynamicBrowser.vue';
import {nextTick} from 'vue';
import {
  ActionService,
  BaseModel,
  HitBannerNotificationItem,
  Menu,
  ProjectPart,
  DataService,
  useConfigurationStore,
  useNotificationsStore,
  useUserProfileStore,
  usePluginStore,
  useNeighborNavigationStore,
  getNeighborTabbedListPanelContext,
} from '@hit/base';
import {HitUtils} from '@hit/components/src/utils/hit/HitUtils';
import {CustomCodeUtils} from '../../../helpers/iframe';
import HitTopIframe from '@hit/components/src/components/iframe/HitTopIframe.vue';
import HitPopupIframe from '@hit/components/src/components/iframe/HitPopupIframe.vue';
import HitTabIframe from '@hit/components/src/components/iframe/HitTabIframe.vue';
import HitAppPrintConfig from '../../app/print/HitAppPrintConfig.vue';
import SaveAsFavoriteModal from '../../modals/global/SaveAsFavoriteModal.vue';
import PanelsConfig from '../../../config/panels';
import {mapActions} from 'pinia';
import {HitOnlineEnums} from '../../../helpers/constants';
import {getFkColumnName} from '@hit/base/src/components/container/HitContainerHelpers';
import {defineAsyncComponent} from 'vue';

export default {
  name: 'HitFloatingPanel',
  components: {
    HitAppShowCreatedModified,
    HitDialog,
    HitIcon,
    HitButton,
    HitDropdown,
    CreateItemDialog,
    HitDynamicBrowser,
    HitTopIframe,
    HitPopupIframe,
    HitTabIframe,
    HitAppPrintConfig,
    SaveAsFavoriteModal,
    HitModal,
    HitInputCheckbox,
    HitInputRadioButton,
    HitAppImportExcel: defineAsyncComponent(
      () => import('../../app/common/HitAppImportExcel.vue'),
    ),
  },
  setup() {
    const {t} = useI18n();
    const config = useConfigurationStore();
    const user = useUserProfileStore();
    const plugin = usePluginStore();
    return {t, user, config, plugin};
  },
  mixins: [HitBreakpointsMixin],
  inject: ['reloadPanels'],
  props: {
    /**
     * When we are on a details screen, we can pass the object from the model
     * Otherwise, we pass a string that is displayed in the floating panel
     */
    selectedInstance: {
      type: [String, Object],
      default: '',
    },

    /**
     * When we are not in a detail screen but in a list screen, we can pass the
     * data model class to access the necessary getters for create-item-config, etc.
     */
    dataObjectClass: {
      type: Function,
      required: false,
      default: null,
    },
    navigationItems: {
      type: Array,
      required: true,
    },
    displayHome: {
      type: Boolean,
      default: true,
    },
    routeParamId: {
      type: String,
      default: 'id',
    },
    primaryActions: {
      type: Array,
      default() {
        return [];
      },
    },
    secondaryActions: {
      type: Array,
      default() {
        return [];
      },
    },
    otherActions: {
      type: Array,
      default() {
        return [];
      },
    },
    // On desktop = like primary buttons, on mobile phone = like panels to make access easier
    callToActionButtons: {
      type: Array,
      required: false,
      default: () => [],
    },
    canvas: {
      type: Object,
      required: false,
    },
    forceCreateItemDialogVisible: {
      type: Boolean,
      default: false,
    },

    /**
     * Allows the parent component to explicitly hide the print button
     * even if a print template type exists on the selected instance.
     */
    hidePrintButton: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * The default columns are defined by the model class of the object. This prop can be used to pass some custom columns
     */
    customInitCols: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },

    /**
     * The print function is configured inside the entity model but in some cases
     * we want to override it
     */
    forcePrintTemplateType: {
      type: String,
      required: false,
      default: null,
    },

    /**
     * The deletable config is made inside the model definition for this entity
     * THis flag can force the delete button to appear
     */
    forceDeletable: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * The addable config is made inside the model definition for this entity
     * THis flag can force the add button to appear
     */
    forceAddable: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * Function to filter the templates depending on other states
     */
    customTemplateFilterFn: {
      type: Function,
      required: false,
      default: null,
    },
    locked: {
      type: Boolean,
      required: false,
      default: false,
    },
    activeJumpEntity: {
      type: Boolean,
      required: false,
      default: true,
    },
    printConfigsInitialValues: {
      type: Object,
      required: false,
      default: null,
    },
    printConfigOptions: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    tab: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      DropdownItem, // expose to template for :items="[new DropdownItem(...)]"
      displayOtherActionsDropdown: false,
      showCreateDialog: false,
      showDeleteDialog: false,
      showPrintDialog: false,
      showImportDialog: false,
      showDuplicateDialog: false,
      showCreationModificationPopup: false,
      browserModalVisible: false,
      teleportReady: false,
      homeMenu: new Menu('', 'home', 'hit-app.common.home', 'home'),
      customUi: undefined,
      customActions: [],
      actionOverrides: [],
      pendingAction: null,
      iframeData: null,
      showFavoriteModal: false,
      showHidePanelsModal: false,
      hidePanelFormData: {},
      previousRecordId: null,
      nextRecordId: null,
      optionsDuplicate: [
        {
          label: this.t('hit-app.common.follow-automatic-number'),
          id: 'automatic-number',
        },
        {
          label: this.t('hit-app.common.add-copy'),
          id: 'copy',
        },
      ],
      duplicateValue: 'automatic-number',
    };
  },
  computed: {
    /** Checks if the current route is a favorite */
    isFavorite() {
      const favUrl = this.$router.currentRoute._value.fullPath;
      return this.config.favorites.find((f) => f.url === favUrl);
    },

    allViewPanels() {
      const viewPanels = PanelsConfig.getAllViewPanels(
        this.$router.currentRoute._value.name,
      );

      const accessiblePanels = viewPanels.filter((panel) => {
        if (!panel.moduleKeys || panel.moduleKeys.length === 0) {
          return true;
        }
        return panel.moduleKeys.every((moduleKey) =>
          this.hasAccessToModule(moduleKey),
        );
      });

      const pluginPanels = this.plugin.getViewPanels(
        this.$router.currentRoute._value.name,
      );
      const customPanelsRaw = this.config.getCustomPanels(
        this.$router.currentRoute._value.name,
      );
      const customPanels = Array.isArray(customPanelsRaw)
        ? customPanelsRaw
            .filter(
              (panel) =>
                panel &&
                typeof panel === 'object' &&
                panel.id &&
                panel.file &&
                (!Array.isArray(panel.modules) ||
                  panel.modules.every((moduleKey) =>
                    this.hasAccessToModule(moduleKey),
                  )),
            )
            .map((panel) => ({
              id: `custom-panel:${String(panel.id)}`,
              key: panel.label || String(panel.id),
            }))
        : [];
      const allPanels = accessiblePanels.concat(pluginPanels, customPanels);

      return allPanels.map((panel) => ({
        id: panel.id,
        key: panel.key,
        hidden: this.config
          .getHiddenPanels(this.$router.currentRoute._value.name)
          .includes(panel.id),
      }));
    },

    HitUtils() {
      return HitUtils;
    },
    /**
     * ID of the record we are working on
     */
    instanceId() {
      return this.$route.params.id;
    },

    /**
     * Checks if the passed value to the selected instance is from the new model
     * based logic
     */
    isModelBased() {
      return (
        this.selectedInstance instanceof BaseModel ||
        (this.dataObjectClass &&
          this.dataObjectClass.prototype instanceof BaseModel)
      );
    },

    /**
     * Checks if the passed value for the instance is an instance of the model
     * -> in this case we are on the detail screen and can display delete, etc
     */
    hasPassedModelInstance() {
      return this.selectedInstance instanceof BaseModel;
    },

    isReadOnlyImportedInstance() {
      const customData = this.selectedInstance?.customData;
      if (!customData) {
        return false;
      }
      return (
        customData.hitoffice_origin !== undefined ||
        customData.basic_resource_library === true
      );
    },

    readOnlyImportedTooltip() {
      if (this.selectedInstance?.customData?.basic_resource_library === true) {
        return this.t('hit-app.resource.locked-basic-library');
      }
      return this.t('hit-app.common.locked');
    },

    /**
     * The entity class can be defined on two different ways:
     * selected instance is a data object model
     * we pass the class to the floating panel (useful for list views)
     */
    entityClass() {
      if (this.hasPassedModelInstance) {
        return this.selectedInstance.constructor;
      }
      return this.dataObjectClass;
    },

    /**
     * If it is a data object model -> use fullDesignation of entity
     * Otherwise, we simply display the passed string
     */
    selectedInstanceDesignationToDisplay() {
      if (this.hasPassedModelInstance) {
        return this.selectedInstance.entityDesignation;
      }
      return this.selectedInstance;
    },

    /**
     * When the browser is opened for a project part, we only display project
     * parts from the project the actual project part belongs to
     */
    browserFilterColumn() {
      return this.selectedInstance instanceof ProjectPart ? 'project_id' : null;
    },

    browserFilterValue() {
      return this.selectedInstance instanceof ProjectPart
        ? this.selectedInstance.project.id
        : null;
    },

    /**
     * In most cases, the item to print is the item from the detail page but there
     * are some exceptions like:
       - action summary -> item to print is the connected user
     */
    detailPrintId() {
      switch (this.forcePrintTemplateType) {
        case 'actionSummary':
          return this.user.keycloakId;
        case 'stock':
          return '*';
        case 'resourceList':
          return '*';
        default:
          return this.instanceId;
      }
    },

    /**
     * The text displayed in the create new entity modal depends on the entity type
     */
    addItemText() {
      if (this.entityClass) {
        const entity = HitUtils.camelToKebab(this.entityClass.apiRoute);
        const formatted_entity = entity.replace('_', '-');
        return this.t(`hit-app.${formatted_entity}.add-${formatted_entity}`);
      }
      return '';
    },

    /**
     * The primary buttons contain the reordering functionality and a create
     * button when the floating panel comes from an entity page
     */
    primaryButtonsComponentLevel() {
      const buttons = [];
      if (
        (this.entityClass && this.entityClass?.config?.addable) ||
        this.forceAddable
      ) {
        buttons.push(
          new HitActionItem('add-item', this.addItemText, 'add', {
            buttonColor: 'accent',
            // active: !this.config.hasSyncActivated(this.entityClass?.apiRoute),
          }),
        );
      }
      if (this.canvas && this.canvas.showReorderButtons) {
        buttons.push(
          new HitActionItem(
            'finish-reordering',
            this.t('hit-app.common.finish-reordering'),
            'check',
          ),
        );
      }
      return buttons;
    },
    secondaryButtonsComponentLevel() {
      const buttons = [];
      if (
        !this.hidePrintButton &&
        ((this.isModelBased && this.selectedInstance?.printTemplateType) ||
          this.forcePrintTemplateType)
      ) {
        buttons.push(
          new HitActionItem(
            'print',
            this.t('hit-base.common.print'),
            'printer',
          ),
        );
      }
      if (
        this.selectedInstance instanceof BaseModel &&
        this.responsiveBreakpointLg &&
        this.activeJumpEntity
      ) {
        buttons.push(
          new HitActionItem(
            'previous',
            this.t('hit-components.common.previous'),
            'expand-left',
            {
              buttonColor: 'button-floating',
              hideLabelOnLgLayout: true,
              active: this.previousRecordId !== null,
            },
          ),
        );
      }
      if (this.isModelBased && this.selectedInstance?.config?.browsable) {
        buttons.push(
          new HitActionItem(
            'browse',
            this.t('hit-components.common.search'),
            'search',
            {buttonColor: 'button-floating', hideLabelOnLgLayout: true},
          ),
        );
      }
      if (
        this.selectedInstance instanceof BaseModel &&
        this.responsiveBreakpointLg &&
        this.activeJumpEntity
      ) {
        buttons.push(
          new HitActionItem(
            'next',
            this.t('hit-components.common.next'),
            'expand-right',
            {
              buttonColor: 'button-floating',
              hideLabelOnLgLayout: true,
              active: this.nextRecordId !== null,
            },
          ),
        );
      }
      if (this.isModelBased && this.selectedInstance.detailPageRoute) {
        buttons.push(
          new HitActionItem(
            'show-info',
            this.t('hit-base.common.info'),
            'info',
            {buttonColor: 'button-floating', hideLabelOnLgLayout: true},
          ),
        );
      }
      if (
        (this.isModelBased && this.selectedInstance?.config?.deletable) ||
        this.forceDeletable
      ) {
        buttons.push(
          new HitActionItem(
            'ask-delete',
            this.t('hit-components.common.delete'),
            'trash',
            {buttonColor: 'button-floating', hideLabelOnLgLayout: true},
          ),
        );
      }
      return buttons;
    },
    otherActionComponentLevel() {
      const actions = [];
      if (this.isModelBased && this.entityClass?.config?.importable) {
        actions.push(
          new HitActionItem(
            'import-excel',
            this.t('hit-components.common.import'),
            'import',
          ),
        );
      }
      if (
        this.isModelBased &&
        this.entityClass?.apiRoute === 'resource' &&
        !this.hasPassedModelInstance &&
        this.hasAccessToModule('basic_resource_library')
      ) {
        actions.push(
          new HitActionItem(
            'import-basic-resource-library',
            this.t('hit-app.resource.import-from-basic-resource-library'),
            'import',
          ),
        );
      }
      if (
        this.isModelBased &&
        this.entityClass?.apiRoute === 'resource' &&
        !this.hasPassedModelInstance &&
        this.hasAccessToModule('datanorm')
      ) {
        actions.push(
          new HitActionItem(
            'import-datanorm',
            this.t('hit-app.resource.import-datanorm'),
            'import',
          ),
        );
      }
      if (
        this.isModelBased &&
        this.selectedInstance?.config?.duplicatable &&
        !this.selectedInstance.locked
      ) {
        actions.push(
          new HitActionItem(
            'duplicate',
            this.t('hit-base.common.duplicate'),
            'duplicate',
          ),
        );
      }
      if (this.canvas && !this.canvas.showReorderButtons) {
        actions.push(
          new HitActionItem(
            'start-reordering',
            this.t('hit-app.common.modify-panel-order'),
            'shuffle',
          ),
        );
      }
      if (this.allViewPanels.length > 1) {
        actions.push(
          new HitActionItem(
            'hide-panels',
            this.t('hit-app.common.hide-panels'),
            'show-off',
          ),
        );
      }
      actions.push(
        new HitActionItem(
          'save-as-favorite',
          this.isFavorite
            ? this.t('hit-app.configuration.remove-favorite')
            : this.t('hit-app.configuration.save-as-favorite'),
          'favorite',
        ),
      );
      return actions;
    },
    visibleCallToActionButtons() {
      return this.callToActionButtons.filter((action) => !action.disabled);
    },
    desktopCallToActionButtons() {
      return this.responsiveBreakpointSm ? this.visibleCallToActionButtons : [];
    },
    actionButtons() {
      if (this.responsiveBreakpointLg) {
        return this.primaryButtonsComponentLevel
          .concat(this.primaryActions)
          .concat(this.desktopCallToActionButtons)
          .concat(this.secondaryActions)
          .concat(this.secondaryButtonsComponentLevel);
      } else {
        return this.primaryButtonsComponentLevel
          .concat(this.primaryActions)
          .concat(this.desktopCallToActionButtons);
      }
    },
    otherMenuActions() {
      if (this.responsiveBreakpointLg) {
        return this.otherActions.concat(this.otherActionComponentLevel);
      } else {
        return this.secondaryButtonsComponentLevel
          .concat(this.secondaryActions)
          .concat(this.otherActions)
          .concat(this.otherActionComponentLevel);
      }
    },
    parentLevels() {
      if (this.navigationItems.length > 0) {
        return this.navigationItems.slice(0, this.navigationItems.length - 1);
      } else {
        return undefined;
      }
    },
    currentLevel() {
      if (this.navigationItems.length > 0) {
        return this.navigationItems[this.navigationItems.length - 1];
      } else {
        return undefined;
      }
    },
    currentItem() {
      if (this.currentLevel.itemId) {
        return this.currentLevel.options.find(
          (elt) => elt.id === this.currentLevel.itemId,
        );
      } else {
        return null;
      }
    },
    previousLevel() {
      if (this.parentLevels && this.parentLevels.length > 0) {
        return this.parentLevels[this.parentLevels.length - 1];
      } else {
        return this.homeMenu;
      }
    },
    previousLevelTo() {
      if (this.parentLevels && this.parentLevels.length > 0) {
        return {
          name: this.previousLevel.route,
          params: this.previousLevel.routeParams,
        };
      } else {
        return this.homeMenu.getRouterTo();
      }
    },
    backButtonLabel() {
      if (this.parentLevels.length > 0) {
        return `${this.previousLevel.label}`;
      } else {
        return `${this.t(this.previousLevel.titleKey)}`;
      }
    },
    allNavigationLevels() {
      let levels = [];
      if (this.displayHome) {
        levels.push(this.homeMenu);
      }
      return levels.concat(this.navigationItems);
    },

    navigationDropdownItems() {
      return this.allNavigationLevels.map(
        (nl, index) =>
          new DropdownItem({
            label: this.t(nl?.label ?? ''),
            separatorAfter: index !== this.allNavigationLevels.length - 1,
            clickEventHandler: () =>
              this.navigateTo(
                nl,
                index === this.allNavigationLevels.length - 1,
              ),
          }),
      );
    },

    customActionsDropdownItems() {
      return this.customActions.map(
        (ca) =>
          new DropdownItem({
            label: ca.label,
            icon: ca.icon,
            clickEventHandler: () => this.onActionClick($event, ca),
          }),
      );
    },
  },
  watch: {
    responsiveBreakpointSm() {
      this.teleportReady = false;
      nextTick(() => {
        this.teleportReady = true;
      });
    },
    responsiveBreakpointHeightMd() {
      this.teleportReady = false;
      nextTick(() => {
        this.teleportReady = true;
      });
    },
    selectedInstance: {
      handler(newValue, oldValue) {
        if (
          newValue instanceof BaseModel &&
          oldValue instanceof BaseModel &&
          newValue.id === oldValue.id &&
          newValue.apiRoute === oldValue.apiRoute
        ) {
          return;
        }
        this.prefetchNeighborRecords();
      },
      immediate: true,
    },
  },
  beforeMount() {
    const configStore = useConfigurationStore();
    const data = configStore.getCustomButtons(this.$route.name);
    this.actionOverrides = configStore.getActionOverrides(this.$route.name);
    this.customUi = [...data];
    data.forEach((button) => {
      let buttonColor = 'success';
      if ('color' in button) buttonColor = `#${button.color}`;
      if ('colour' in button) buttonColor = `#${button.colour}`;
      this.customActions.push(
        new HitActionItem(`custom_${button.id}`, button.label, 'customize', {
          buttonColor: buttonColor,
        }),
      );
    });
  },
  mounted() {
    this.teleportReady = true;
    window.addEventListener(
      'hit-shortcut:create-entry',
      this.onShortcutCreateEntry,
    );
    window.addEventListener(
      'hit-shortcut:next-record',
      this.onShortcutNextRecord,
    );
    window.addEventListener(
      'hit-shortcut:previous-record',
      this.onShortcutPreviousRecord,
    );
    window.addEventListener(
      'hit-shortcut:breadcrumb-up',
      this.onShortcutBreadcrumbUp,
    );
    window.addEventListener(
      'hit-shortcut:confirm-primary-action',
      this.onShortcutConfirmPrimaryAction,
    );
  },
  beforeUnmount() {
    window.removeEventListener(
      'hit-shortcut:create-entry',
      this.onShortcutCreateEntry,
    );
    window.removeEventListener(
      'hit-shortcut:next-record',
      this.onShortcutNextRecord,
    );
    window.removeEventListener(
      'hit-shortcut:previous-record',
      this.onShortcutPreviousRecord,
    );
    window.removeEventListener(
      'hit-shortcut:breadcrumb-up',
      this.onShortcutBreadcrumbUp,
    );
    window.removeEventListener(
      'hit-shortcut:confirm-primary-action',
      this.onShortcutConfirmPrimaryAction,
    );
  },
  methods: {
    ...mapActions(useUserProfileStore, ['hasAccessToModule']),
    navigateTo(level, alreadyOnPage) {
      if (!alreadyOnPage) {
        this.$router.push({
          name: level.route,
          params: level?.routeParams ?? {},
        });
      }
    },
    async deleteEntity() {
      if (!(this.selectedInstance instanceof BaseModel)) {
        return;
      }
      const neighborRecordId = this.nextRecordId ?? this.previousRecordId;
      const deleted = await this.selectedInstance.delete();
      this.showDeleteDialog = false;
      if (deleted === false) {
        return;
      }
      if (neighborRecordId) {
        this.$router.push({
          name: this.entityClass.detailPageRoute,
          params: {id: neighborRecordId},
        });
      } else {
        this.selectedInstance.redirectToList(this.$router);
      }
    },
    isLastSubAction(action, subAction) {
      const subActions = action.subActions;
      return (
        subActions.length > 0 &&
        subActions[subActions.length - 1].id === subAction.id
      );
    },
    isLastAction(action) {
      return (
        this.otherMenuActions.length > 0 &&
        this.otherMenuActions[this.otherMenuActions.length - 1].id === action.id
      );
    },
    getActionLabel(action) {
      if (this.responsiveBreakpointLg && !action.hideLabelOnLgLayout) {
        return action.label;
      } else {
        return null;
      }
    },

    /** Custom popup iframe: close (optional payload). */
    onPopupIframeCancel(payload) {
      this.iframeData = null;
      this.pendingAction = null;
      if (payload !== undefined && payload !== null) {
        this.$emit('popup-iframe-closed', payload);
      }
    },
    /** Action override iframe: proceed with the native button action. */
    onPopupIframeContinue(payload) {
      const pending = this.pendingAction;
      this.iframeData = null;
      this.pendingAction = null;
      if (!pending) {
        return;
      }
      if (payload !== undefined && payload !== null) {
        this.$emit('popup-iframe-continued', payload);
      }
      this.onActionClick(
        {stopImmediatePropagation: () => {}},
        {id: pending.id},
        pending.middleClick,
        true,
      );
    },
    getActionOverrideFor(actionId) {
      return (this.actionOverrides || []).find(
        (override) =>
          override.actionId === actionId && override.enabled !== false,
      );
    },
    openActionOverrideIframe(override, action) {
      this.iframeData = null;
      setTimeout(() => {
        const customUtils = new CustomCodeUtils(this, this.$router);
        customUtils.executeActionOverride(override, action);
      }, 5);
    },
    /** Custom popup iframe: navigate (path or name+params). */
    onPopupIframeNavigate({path, name, params}) {
      if (path) {
        this.$router.push(path);
      } else if (name) {
        this.$router.push({name, params: params || {}});
      }
    },
    /** Custom popup iframe: refresh current view. */
    onPopupIframeRefresh() {
      this.$router.replace({path: this.$route.fullPath});
    },
    /** Custom popup iframe: show banner notification. */
    onPopupIframeNotify({level, message}) {
      const levelMap = {
        success: HitBannerNotificationItem.SUCCESS,
        error: HitBannerNotificationItem.DANGER,
        warning: HitBannerNotificationItem.WARNING,
        info: HitBannerNotificationItem.INFO,
      };
      useNotificationsStore().insertBannerNotification(
        message,
        levelMap[level] || HitBannerNotificationItem.INFO,
      );
    },
    /** Custom popup iframe: update modal title. */
    onPopupIframeSetTitle(title) {
      if (this.iframeData && typeof title === 'string') {
        this.iframeData.title = title;
      }
    },
    /** Custom popup iframe: open URL in new tab or self. */
    onPopupIframeOpenUrl({url, target = '_blank'}) {
      window.open(url, target);
    },
    /** Custom popup iframe: trigger file download in parent context. */
    onPopupIframeDownloadFile({
      filename = 'download.bin',
      mimeType = 'application/octet-stream',
      base64,
      url,
    }) {
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }
      if (!base64) return;
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], {type: mimeType});
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    },

    /**
     * When an entity is selected in the redirect browser, we redirect the user
     * to the detail page of this entity
     */
    redirectToBrowsedItem(item) {
      this.$router.push({
        name: this.entityClass.detailPageRoute,
        params: {id: item.id},
      });
    },

    /**
     * Duplicates the record in the database by adding NEW suffix to no
     * Then redirects to the newly created record
     */
    duplicateRecord() {
      ActionService.post(
        `helpers/duplicate/${this.entityClass.apiRoute}/${this.instanceId}`,
        {
          values: {
            duplication_mode: this.duplicateValue,
          },
        },
      ).then((res) => {
        const recordId = res.data;
        this.showDuplicateDialog = false;
        this.$router.push({
          name: this.entityClass.detailPageRoute,
          params: {id: recordId},
        });
      });
    },

    executeActionById(actionId, middleClick = false) {
      const stubEvent = {stopImmediatePropagation: () => {}};
      this.onActionClick(stubEvent, {id: actionId}, middleClick);
    },
    onShortcutCreateEntry() {
      const canCreate =
        (this.entityClass && this.entityClass?.config?.addable) ||
        this.forceAddable;
      if (canCreate) {
        this.executeActionById('add-item');
      }
    },
    onShortcutNextRecord() {
      if (this.nextRecordId) {
        this.executeActionById('next');
      }
    },
    onShortcutPreviousRecord() {
      if (this.previousRecordId) {
        this.executeActionById('previous');
      }
    },
    onShortcutBreadcrumbUp() {
      if (!this.previousLevelTo) {
        return;
      }
      this.$router.push(this.previousLevelTo);
    },
    onShortcutConfirmPrimaryAction() {
      const prioritizedActionIds = [
        'validate',
        'save',
        'submit',
        'ok',
        'finish-reordering',
      ];
      const availableActions = this.actionButtons.concat(this.otherMenuActions);
      const candidateAction = availableActions.find((action) =>
        prioritizedActionIds.includes(action.id),
      );
      if (candidateAction) {
        this.executeActionById(candidateAction.id);
      }
    },
    onActionClick($event, action, middleClick = false, skipOverride = false) {
      this.displayOtherActionsDropdown = false;
      $event.stopImmediatePropagation();
      if (action.id.includes('custom')) {
        // TODO: solve reactivity bug to remove timeout of 5ms -> otherwise iframe does not re-render
        this.iframeData = null;
        setTimeout(() => {
          const customUtils = new CustomCodeUtils(this, this.$router);
          customUtils.executeCustomCode(this.customUi, action);
        }, 5);
        return;
      }
      if (!skipOverride) {
        const override = this.getActionOverrideFor(action.id);
        if (override) {
          this.pendingAction = {
            id: action.id,
            middleClick,
            mode: override.mode || 'preHook',
          };
          this.openActionOverrideIframe(override, action);
          return;
        }
      }
      switch (action.id) {
        case 'add-item':
          this.showCreateDialog = true;
          break;
        case 'ask-delete':
          this.showDeleteDialog = true;
          break;
        case 'print':
          this.showPrintDialog = true;
          break;
        case 'show-info':
          this.showCreationModificationPopup = true;
          break;
        case 'import-excel':
          this.showImportDialog = true;
          break;
        case 'browse':
          this.browserModalVisible = true;
          break;
        case 'previous':
          this.switchRecord('previous');
          break;
        case 'next':
          this.switchRecord('next');
          break;
        case 'start-reordering':
          this.canvas.startReordering();
          break;
        case 'finish-reordering':
          this.canvas.finishReordering();
          break;
        case 'duplicate':
          this.showDuplicateDialog = true;
          break;
        case 'save-as-favorite':
          this.showFavoriteModal = true;
          break;
        case 'hide-panels':
          this.showHidePanelsModal = true;
          this.hidePanelFormData = [...this.allViewPanels];
          break;
        default:
          if (action.routeName) {
            this.$router.push({
              name: action.routeName,
              params: action.routeParams ?? {},
            });
            return;
          }
          this.$emit('action', action.id, middleClick);
      }
    },
    onCallToActionClick(action) {
      this.onActionClick({stopImmediatePropagation: () => {}}, action);
    },
    hideCreateItemDialog() {
      this.showCreateDialog = false;
      this.$emit('hideCreateDialog');
    },
    togglePanel(panel) {
      const panelToToggle = this.hidePanelFormData.find(
        (p) => p.id === panel.id,
      );
      panelToToggle.hidden = !panelToToggle.hidden;
    },
    saveHidePanels() {
      const arrayToSave = [];
      this.hidePanelFormData.forEach((panel) => {
        if (panel.hidden) arrayToSave.push(panel.id);
      });
      this.config.updateHiddenPanels(
        this.$router.currentRoute._value.name,
        arrayToSave,
      );
      this.showHidePanelsModal = false;
      this.reloadPanels(true);
    },
    getListConfigKey() {
      const apiRoute = this.selectedInstance?.apiRoute;
      if (!apiRoute) {
        return null;
      }
      return `${apiRoute}.list`;
    },
    getFilterColumn(propertyName) {
      // Qualification columns use the qualification UUID as their "propertyName".
      // The DB doesn't have a column named by the UUID; we keep the UUID so the
      // neighbor procedure can interpret it as qualification_id + staff_id in WHERE.
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (typeof propertyName === 'string' && uuidRegex.test(propertyName)) {
        return propertyName;
      }

      const allAttributes = this.entityClass?.allAttributes ?? [];
      const directAttribute = allAttributes.find(
        (att) => att.name === propertyName,
      );
      if (
        directAttribute?.dbColumn &&
        typeof directAttribute.dbColumn === 'string' &&
        !directAttribute.dbColumn.includes('(')
      ) {
        return directAttribute.dbColumn;
      }
      // Attribute exists but has no explicit column -> DB column equals attribute key
      // (example: street, postcode, city, ...). FK attributes (>100) use *_id on the table.
      if (Object.prototype.hasOwnProperty.call(allAttributes, propertyName)) {
        if (directAttribute instanceof AT.FkBaseAttribute) {
          try {
            return getFkColumnName(directAttribute, propertyName);
          } catch {
            return propertyName;
          }
        }
        return propertyName;
      }
      // Some filters can use DB column names directly (e.g. designation)
      const aliasAttribute = Object.values(allAttributes).find(
        (attribute) =>
          attribute?.column &&
          typeof attribute.column === 'string' &&
          !attribute.column.includes('(') &&
          attribute.column === propertyName,
      );
      if (aliasAttribute?.column) {
        return aliasAttribute.column;
      }
      for (const [attributeKey, attribute] of Object.entries(allAttributes)) {
        if (!attribute || attribute.dataType < 101) {
          continue;
        }
        try {
          if (getFkColumnName(attribute, attributeKey) === propertyName) {
            return propertyName;
          }
        } catch {
          // non-FK attribute types in the >100 range
        }
      }
      return null;
    },
    getSortColumn(sortProperty) {
      if (!sortProperty) {
        return 'no';
      }
      return this.getFilterColumn(sortProperty) || sortProperty;
    },
    normalizeFilterValue(op, value) {
      if (value === undefined || value === null) {
        return value;
      }
      // FK filters can be stored as selected objects ({id, ...}) in some views.
      // The SQL procedure expects scalar values (uuid/text), so we normalize to id.
      if (typeof value === 'object' && !Array.isArray(value)) {
        if (Object.prototype.hasOwnProperty.call(value, 'id')) {
          return value.id;
        }
      }
      if (op === '∋' || op === '∌') {
        const stringValue = String(value).trim();
        if (!stringValue) {
          return null;
        }
        return `%${stringValue}%`;
      }
      return value;
    },
    formatFiltersForNeighborProcedure(filters) {
      if (!filters || typeof filters !== 'object') {
        return [];
      }
      const result = [];
      Object.entries(filters).forEach(([propertyName, operators]) => {
        const column = this.getFilterColumn(propertyName);
        if (!column || !operators || typeof operators !== 'object') {
          return;
        }
        Object.entries(operators).forEach(([op, value]) => {
          if (value === undefined || value === '' || value === null) {
            return;
          }
          if (Array.isArray(value)) {
            if (op === '∈') {
              const normalizedValues = value
                .filter(
                  (item) => item !== undefined && item !== null && item !== '',
                )
                .map((item) => this.normalizeFilterValue(op, item))
                .filter((v) => v !== undefined && v !== null && v !== '');
              if (normalizedValues.length > 0) {
                result.push({
                  column,
                  op,
                  value: normalizedValues,
                });
              }
              return;
            }
            value
              .filter(
                (item) => item !== undefined && item !== null && item !== '',
              )
              .forEach((item) => {
                const normalizedValue = this.normalizeFilterValue(op, item);
                if (
                  normalizedValue === undefined ||
                  normalizedValue === null ||
                  normalizedValue === ''
                ) {
                  return;
                }
                result.push({
                  column,
                  op,
                  value: normalizedValue,
                });
              });
            return;
          }
          const normalizedValue = this.normalizeFilterValue(op, value);
          if (
            normalizedValue === undefined ||
            normalizedValue === null ||
            normalizedValue === ''
          ) {
            return;
          }
          result.push({
            column,
            op,
            value: normalizedValue,
          });
        });
      });
      return result;
    },
    buildProjectPartStatusFilter(column) {
      const isQuotationFilter = column === 'is_quotation';
      const prefix = isQuotationFilter ? 'QUOTATION_' : 'WORK_';
      const statusValues = HitOnlineEnums.PROJECT_PART_STATUS.filter((status) =>
        status.id.startsWith(prefix),
      ).map((status) => status.id);
      if (statusValues.length === 0) {
        return {};
      }
      return {status: {'∈': statusValues}};
    },
    getNeighborContext() {
      const defaultSort = this.selectedInstance.getDefaultSort?.() ?? {
        property: 'no',
        order: 'asc',
      };
      const apiRoute = this.selectedInstance.apiRoute;
      const itemId = this.selectedInstance.id;
      const listConfigKey = this.getListConfigKey();
      const tabbedListPanel = getNeighborTabbedListPanelContext(
        apiRoute,
        this.config,
      );

      const activeFiltersFromList = tabbedListPanel
        ? this.config.getTableFilters(
            tabbedListPanel.panelId,
            tabbedListPanel.suffix,
          )
        : this.config.getTableFilters(listConfigKey);
      const activeFiltersFromApi = this.config.getTableFilters(apiRoute);
      const isNonEmptyFilters = (f) => {
        if (!f) return false;
        if (Array.isArray(f)) return f.length > 0;
        if (typeof f === 'object') return Object.keys(f).length > 0;
        return false;
      };
      const activeFilters = isNonEmptyFilters(activeFiltersFromList)
        ? activeFiltersFromList
        : activeFiltersFromApi;

      const sortingFromList = tabbedListPanel
        ? this.config.getTableSorting(
            tabbedListPanel.panelId,
            tabbedListPanel.suffix,
          )
        : this.config.getTableSorting(listConfigKey);
      const sortingFromApi = this.config.getTableSorting(apiRoute);

      const neighborStore = useNeighborNavigationStore();
      let neighborFromStore =
        neighborStore.getFiltersObjectForApiRoute(apiRoute);
      if (neighborFromStore && typeof neighborFromStore === 'object') {
        neighborFromStore = {...neighborFromStore};
        if (apiRoute === 'project_part') {
          if (neighborFromStore.is_quotation?.['='] === true) {
            Object.assign(
              neighborFromStore,
              this.buildProjectPartStatusFilter('is_quotation'),
            );
            delete neighborFromStore.is_quotation;
          }
          if (neighborFromStore.is_work?.['='] === true) {
            Object.assign(
              neighborFromStore,
              this.buildProjectPartStatusFilter('is_work'),
            );
            delete neighborFromStore.is_work;
          }
        }
      }
      const hasNeighborScope =
        neighborFromStore &&
        typeof neighborFromStore === 'object' &&
        Object.keys(neighborFromStore).length > 0;

      const sortingFromContainer =
        hasNeighborScope && neighborStore.getTableSortingForApiRoute(apiRoute);
      const hasSorting = (s) =>
        s && typeof s === 'object' && s.property && s.order;
      const sorting = hasSorting(sortingFromList)
        ? sortingFromList
        : hasSorting(sortingFromContainer)
          ? sortingFromContainer
          : hasSorting(sortingFromApi)
            ? sortingFromApi
            : null;

      // List on tabs: the user filters are in Pinia (e.g. action.list.filters.list);
      // the neighbor store only takes filterId + customFilters at click — we need to merge.
      // Without tabbedListPanel, we keep only the neighbor to avoid filters from another screen.
      let allActiveFilters;
      if (hasNeighborScope) {
        allActiveFilters = tabbedListPanel
          ? {...neighborFromStore, ...(activeFilters || {})}
          : {...neighborFromStore};
      } else {
        allActiveFilters = {
          ...(activeFilters || {}),
          ...(neighborFromStore || {}),
        };
      }

      const sortProperty = sorting?.property || defaultSort.property || 'no';
      const sortColumn = this.getSortColumn(sortProperty);
      const sortOrder = (
        sorting?.order ||
        defaultSort.order ||
        'asc'
      ).toLowerCase();

      // Tri par projet : le voisin doit rester parmi les lots du même projet (WHERE project_id = …).
      if (
        apiRoute === 'project_part' &&
        sortColumn === 'project_id' &&
        this.selectedInstance instanceof ProjectPart
      ) {
        const projectId =
          this.selectedInstance.project?.id ?? this.selectedInstance.project_id;
        if (projectId) {
          allActiveFilters = {
            ...allActiveFilters,
            project: {'=': projectId},
          };
        }
      }

      const procedureFilters =
        this.formatFiltersForNeighborProcedure(allActiveFilters);

      return {
        apiRoute,
        itemId,
        sortColumn,
        sortOrder,
        procedureFilters,
      };
    },
    async getNeighborRecordId(direction) {
      const context = this.getNeighborContext();

      const procedurePayload = {
        p_table: context.apiRoute,
        p_column: context.sortColumn,
        p_id: context.itemId,
        p_direction: direction,
        p_sort_order: context.sortOrder,
        p_filters: context.procedureFilters,
      };
      const response = await DataService.procedure(
        'get_neighbor_dynamic',
        procedurePayload,
        {},
      );
      return response?.data ?? null;
    },
    async prefetchNeighborRecords() {
      this.previousRecordId = null;
      this.nextRecordId = null;
      if (!(this.selectedInstance instanceof BaseModel)) {
        return;
      }
      try {
        const [previousRecordId, nextRecordId] = await Promise.all([
          this.getNeighborRecordId('previous'),
          this.getNeighborRecordId('next'),
        ]);
        this.previousRecordId = previousRecordId;
        this.nextRecordId = nextRecordId;
      } catch (error) {
        console.error('Error while prefetching neighbors:', error);
      }
    },
    async switchRecord(direction) {
      const cachedRecordId =
        direction === 'previous' ? this.previousRecordId : this.nextRecordId;
      if (cachedRecordId) {
        this.$router.push({
          name: this.entityClass.detailPageRoute,
          params: {id: cachedRecordId},
        });
        return;
      }

      try {
        const recordId = await this.getNeighborRecordId(direction);
        if (!recordId) {
          return;
        }

        this.$router.push({
          name: this.entityClass.detailPageRoute,
          params: {id: recordId},
        });
      } catch (error) {
        console.error('Error while switching record:', error);
        useNotificationsStore().insertBannerNotification(
          this.t('hit-base.banners.unknown-error-title'),
          HitBannerNotificationItem.DANGER,
        );
      }
    },
  },
};
</script>

<style scoped lang="scss">
.container {
  display: flex;
  flex-direction: column;
}

.floating-bottom {
  display: grid;
  grid-template-columns: auto max-content max-content;
}

.split-button-chevron :deep(button) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: 0;
}

.floating-call-to-action-card {
  grid-template-columns: 4rem 1fr;
}
</style>
